import React, {useState, useRef, useEffect} from 'react';
import {basicSetup} from "codemirror";
import {EditorView, placeholder} from "@codemirror/view";
import {EditorState, Compartment} from "@codemirror/state";
import {json, jsonLanguage, jsonParseLinter} from "@codemirror/lang-json";
import {linter, lintGutter} from "@codemirror/lint";
import {oneDark} from "@codemirror/theme-one-dark";
import {Tooltip} from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

const STORAGE_KEY = "jsonvisual:doc";
const SHARE_LIMIT = 50000; // chars; larger payloads make unwieldy URLs

// Sizing/scroll theme applied in BOTH light and dark mode. Kept out of the
// swappable color compartment so the editor stays a fixed height and scrolls
// internally instead of growing with the content.
const baseTheme = EditorView.theme({
  "&": {
      textAlign: "left",
      height: "350px",
      borderStyle: "groove"
  },
  ".cm-scroller": {
      overflow: "auto"
  },
})

const linterConf = new Compartment()
const themeConf = new Compartment()

const dummyLinter = () => (view) => {
    return [];
};

const autolinterConf = EditorState.transactionExtender.of(tr => {
    if (!tr.docChanged) return null
    let docIsHTML = tr.newDoc.toString()
    return {
        effects: linterConf.reconfigure(docIsHTML === ""? linter(dummyLinter()): linter(jsonParseLinter()))
    }
})

// Initial document: a shared URL (#json=...) wins, then the last saved draft.
const getInitialDoc = () => {
    try {
        const hash = window.location.hash;
        const match = hash.match(/[#&]json=([^&]+)/);
        if (match) return decodeURIComponent(match[1]);
    } catch (e) { /* ignore malformed hash */ }
    try {
        return window.localStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
        return '';
    }
}


const InputComponent = ({sampleJson, setJsonState, darkMode}) => {
    const [jsonText, setJsonText] = useState('')
    const [topMsg, setTopMsg] = useState('')
    const [msgType, setMsgType] = useState(0)
    const [view, setView] = useState(null)
    const editor = useRef();
    const fileInput = useRef();

    useEffect(()=>{
        const initialDoc = getInitialDoc()
        let view = new EditorView({
          state: EditorState.create({
              doc: initialDoc,
              extensions: [
                  basicSetup,
                  baseTheme,
                  themeConf.of(darkMode ? oneDark : []),
                  json(),
                  placeholder('Start typing here or paste JSON here'),
                  jsonLanguage,
                  lintGutter(),
                  linterConf.of(linter(dummyLinter())),
                  autolinterConf,
                  EditorView.updateListener.of((v)=>{
                    if (v.docChanged) {
                        onChangeTextArea(v)
                    }
                  })
              ]
          }),
          parent: editor.current
        })
        setView(view)
        // Surface any restored draft immediately (state + validity message).
        if (initialDoc !== '') {
            setJsonText(initialDoc)
            validate(initialDoc)
        }
        return () => {
              view.destroy();
            };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(()=>{
        if (sampleJson!==undefined && view) {
            const text = JSON.stringify(sampleJson, null, 2)
            setJsonText(text)
            replaceDoc(text)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sampleJson])

    // Swap the editor's color theme when dark mode toggles.
    useEffect(()=>{
        if (view) {
            view.dispatch({
                effects: themeConf.reconfigure(darkMode ? oneDark : [])
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [darkMode])

    // Replace the whole editor document with `text`.
    const replaceDoc = (text) => {
        if (!view) return
        const endPosition = view.state.doc.length;
        view.dispatch({
            changes: { from: 0, to: endPosition, insert: text }
        })
    }

    const persist = (value) => {
        try {
            if (value) window.localStorage.setItem(STORAGE_KEY, value)
            else window.localStorage.removeItem(STORAGE_KEY)
        } catch (e) { /* storage unavailable / quota — ignore */ }
    }

    // Update the validity banner for the given text.
    const validate = (value) => {
        if (value === undefined || value === '') {
            setMsgType(0)
            setTopMsg('')
            return
        }
        try {
            JSON.parse(value)
            setMsgType(1)
            setTopMsg('Valid Json')
        } catch (err) {
            setMsgType(2)
            setTopMsg(err.message)
        }
    }

    const onChangeTextArea = (v) => {
        const value = v.state.doc.toString()
        setJsonText(value)
        persist(value)
        validate(value)
    }

    const onClickSubmit = (e) => {
        e.preventDefault();
        try {
            let parsedJson = JSON.parse(jsonText);
            setJsonState(parsedJson)
        }
        catch(err) {
            setMsgType(2)
            setTopMsg(err.message)
            setJsonState()
        }
    }

    const onClickClear = (e) => {
        e.preventDefault();
        replaceDoc('')
        setMsgType(0)
        setTopMsg('')
        setJsonState()
        setJsonText('')
        persist('')
    }

    const onClickReformat = (e) => {
        e.preventDefault();
        try {
            replaceDoc(JSON.stringify(JSON.parse(view.state.doc.toString()), null, 2))
        }catch (e) {
        }
    }

    const onClickMinify = (e) => {
        e.preventDefault();
        try {
            replaceDoc(JSON.stringify(JSON.parse(view.state.doc.toString())))
        }catch (e) {
        }
    }

    const onClickCopy = (e) => {
        e.preventDefault()
        if (jsonText) {
            navigator.clipboard.writeText(jsonText)
        }
    }

    const onClickDownload = (e) => {
        e.preventDefault()
        if (!jsonText) return
        const blob = new Blob([jsonText], {type: 'application/json'})
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'data.json'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const onClickUpload = (e) => {
        e.preventDefault()
        if (fileInput.current) fileInput.current.click()
    }

    const onFileSelected = (e) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            const text = String(ev.target.result)
            setJsonText(text)
            replaceDoc(text)
            persist(text)
            validate(text)
        }
        reader.readAsText(file)
        // Allow re-selecting the same file later.
        e.target.value = ''
    }

    const onClickShare = (e) => {
        e.preventDefault()
        if (!jsonText) return
        if (jsonText.length > SHARE_LIMIT) {
            setMsgType(2)
            setTopMsg('JSON is too large to share via URL.')
            return
        }
        const base = window.location.href.split('#')[0]
        const link = `${base}#json=${encodeURIComponent(jsonText)}`
        navigator.clipboard.writeText(link)
        setMsgType(1)
        setTopMsg('Shareable link copied to clipboard.')
    }

    const isInvalid = msgType === 2

    return (
        <div className="col-lg-8 ml-auto mr-auto grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <form className="forms-sample">
                  <div className="form-group">
                      <div ref={editor} > </div>
                      {topMsg!==''?<label htmlFor="jsonTextArea"
                             className={msgType===0?"":msgType===1?"text-success":"text-danger"}>
                          {topMsg}
                      </label>:''}
                  </div>
                  <input type="file" accept="application/json,.json" ref={fileInput}
                         onChange={onFileSelected} style={{display: 'none'}} aria-hidden="true"/>
                  <div className={'btn-wrapper'}>
                  <button type="button"
                          className={isInvalid?"btn btn-rounded btn-secondary me-2":"btn-warning btn btn-rounded me-2"}
                          disabled={isInvalid}
                          onClick={onClickReformat}>Reformat</button>
                  <button type="button"
                          className={isInvalid?"btn btn-rounded btn-secondary me-2":"btn-warning btn btn-rounded me-2"}
                          disabled={isInvalid}
                          onClick={onClickMinify}>Minify</button>
                  <button type="button" className="btn btn-outline-secondary btn-rounded btn-icon me-2"
                          aria-label="Copy to Clipboard"
                          data-tooltip-id="jvTip" data-tooltip-content="Copy to Clipboard"
                          onClick={onClickCopy}><i className={'mdi mdi-content-copy text-dark'}></i></button>
                  <button type="button" className="btn btn-outline-secondary btn-rounded btn-icon me-2"
                          aria-label="Upload .json file"
                          data-tooltip-id="jvTip" data-tooltip-content="Upload .json file"
                          onClick={onClickUpload}><i className={'mdi mdi-upload text-dark'}></i></button>
                  <button type="button" className="btn btn-outline-secondary btn-rounded btn-icon me-2"
                          aria-label="Download as .json"
                          data-tooltip-id="jvTip" data-tooltip-content="Download as .json"
                          disabled={!jsonText}
                          onClick={onClickDownload}><i className={'mdi mdi-download text-dark'}></i></button>
                  <button type="button" className="btn btn-outline-secondary btn-rounded btn-icon"
                          aria-label="Copy shareable link"
                          data-tooltip-id="jvTip" data-tooltip-content="Copy shareable link"
                          disabled={!jsonText}
                          onClick={onClickShare}><i className={'mdi mdi-share-variant text-dark'}></i></button>

                  <Tooltip id="jvTip" />

                  <button type="submit"
                        className="btn btn-primary me-2"
                        onClick={event => onClickSubmit(event)}
                        style={{float: 'right'}}>Generate</button>
                  <button type="button"
                          className="btn btn-inverse-danger me-2"
                          onClick={e=>onClickClear(e)}
                          style={{float: 'right'}}>Clear</button>
                  </div>
              </form>
            </div>
          </div>
        </div>
    )
}


export default InputComponent;
